import { EventInput } from "@fullcalendar/core";
import { WorkOrder } from "../../models/models";
import { typeColors, statusOpacity } from "../schedule/calendarStyles";

/** Add minutes to a Date, returns a new Date */
export const addMinutes = (date: Date, minutes: number): Date =>
  new Date(date.getTime() + minutes * 60000);

/** Build the UpdateWorkOrder model preserving all WO fields, overriding only dates.
 *  Optional `overrides` allows callers (e.g. resize handler) to override additional
 *  fields like `estimatedDurationMinutes` beyond the start/end dates. */
export const buildUpdateModel = (
  wo: WorkOrder,
  startDate?: string,
  endDate?: string,
  overrides?: { estimatedDurationMinutes?: number },
) => ({
  title: wo.title,
  description: wo.description,
  workOrderTypeId: wo.workOrderTypeId,
  priorityCode: wo.priorityCode,
  machineId: wo.machineId,
  assignedToUserId: wo.assignedToUserId,
  assignedToUserName: wo.assignedToUserName,
  dueDate: wo.dueDate,
  scheduledStartDate: startDate,
  scheduledEndDate: endDate,
  estimatedDurationMinutes: overrides?.estimatedDurationMinutes ?? wo.estimatedDurationMinutes,
  serviceReportId: wo.serviceReportId,
  parentWorkOrderId: wo.parentWorkOrderId,
  teamId: wo.teamId,
});

/** Default duration (minutes) for events without explicit end/duration info */
const DEFAULT_EVENT_DURATION_MINUTES = 60;

/** Slot boundaries — must match FullCalendar slotMinTime/slotMaxTime in BoardCalendarView */
const SLOT_MIN_HOUR = 6;
const SLOT_MAX_HOUR = 22;

/**
 * Clamp a date's time-of-day into the visible slot range (06:00–21:00).
 * If the hour falls before slotMinTime, move to 08:00 (a reasonable morning slot).
 * If the hour falls after slotMaxTime minus 1h, move to slotMaxTime - 2h (so the 1h block fits).
 * Returns a new Date; does NOT mutate the input.
 */
const clampToVisibleSlot = (d: Date): Date => {
  const clamped = new Date(d.getTime());
  const hour = clamped.getHours();
  if (hour < SLOT_MIN_HOUR) {
    clamped.setHours(8, 0, 0, 0);
  } else if (hour >= SLOT_MAX_HOUR - 1) {
    clamped.setHours(SLOT_MAX_HOUR - 2, 0, 0, 0);
  }
  return clamped;
};

/**
 * Format a Date as a local "YYYY-MM-DDTHH:mm:ss" string (NO timezone suffix).
 * FullCalendar parses strings without a timezone offset as local time, which
 * matches how the .NET backend serialises DateTime (no offset).
 * Using toISOString() would convert to UTC, shifting the time for non-UTC
 * timezones — so we must format manually from local date parts.
 */
export const toLocalDateTimeString = (d: Date): string => {
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const h = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  const s = String(d.getSeconds()).padStart(2, "0");
  return `${y}-${mo}-${day}T${h}:${mi}:${s}`;
};

/**
 * Extract a "YYYY-MM-DD" date-only string from a raw date value.
 * The backend returns DateTime (ScheduledStartDate, DueDate) WITHOUT timezone
 * info (e.g. "2026-03-30T04:00:00"), which JS parses as local time.
 * Using toISOString() would convert to UTC, shifting the date for non-UTC
 * timezones. Instead, extract the date portion from the original string
 * (which always starts with "YYYY-MM-DD") or fall back to local date parts.
 */
const toLocalDateString = (raw: string, dateObj: Date): string => {
  // If the raw string starts with a date-like pattern, extract it directly
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  // Fallback: use local date components (handles DateTimeOffset strings like
  // "2026-03-27T20:27:43+00:00" where the Date object is already correct)
  const y = dateObj.getFullYear();
  const m = String(dateObj.getMonth() + 1).padStart(2, "0");
  const d = String(dateObj.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Map WorkOrder[] to FullCalendar EventInput[] */
export const mapWorkOrdersToEvents = (workOrders: WorkOrder[]): EventInput[] => {
  return workOrders.map((wo) => {
    const typeColor = typeColors[wo.workOrderTypeId] || typeColors[5];
    const opacity = statusOpacity[wo.workOrderStatusId] || 1.0;
    const hasScheduledStart = !!wo.scheduledStartDate;
    const rawStartDate = wo.scheduledStartDate || wo.dueDate || wo.createdDate;
    const rawStartObj = new Date(rawStartDate);

    // Determine if this is a "date-only" event (no meaningful time set).
    // The backend stores DateTime without timezone. JS parses "2026-03-30T00:00:00"
    // as LOCAL midnight, so we must use getHours() (local), not getUTCHours().
    // Date-only WOs are stored as midnight (00:00:00 local) by the frontend's
    // date picker, so local hours/minutes of 0 means "date-only".
    const hasMeaningfulTime = hasScheduledStart &&
      (rawStartObj.getHours() !== 0 || rawStartObj.getMinutes() !== 0);

    let endDate: string | undefined;
    let isAllDay: boolean;

    if (hasMeaningfulTime) {
      // Timed event — position on the time grid.
      // Clamp start into visible slot range so events outside slotMinTime/slotMaxTime
      // still appear (FullCalendar hides events outside the slot range entirely).
      // Preserve original duration when computing end time (don't clamp end independently
      // or we'd get zero-duration events when both start and end are before slotMinTime).
      isAllDay = false;
      const clampedStart = clampToVisibleSlot(rawStartObj);
      if (wo.scheduledEndDate) {
        // Preserve original duration: calculate delta between raw start/end, apply to clamped start
        const rawEnd = new Date(wo.scheduledEndDate);
        const durationMs = rawEnd.getTime() - rawStartObj.getTime();
        const durationMin = Math.max(Math.round(durationMs / 60000), DEFAULT_EVENT_DURATION_MINUTES);
        endDate = toLocalDateTimeString(addMinutes(clampedStart, durationMin));
      } else if (wo.estimatedDurationMinutes) {
        endDate = toLocalDateTimeString(addMinutes(clampedStart, wo.estimatedDurationMinutes));
      } else {
        endDate = toLocalDateTimeString(addMinutes(clampedStart, DEFAULT_EVENT_DURATION_MINUTES));
      }
    } else {
      // Date-only event (scheduled date with no time, or fallback to dueDate/createdDate)
      // Render as all-day so it appears in the all-day row of Week/Day views
      // and on the correct date cell in Month view.
      isAllDay = true;
      endDate = undefined; // all-day events span the full day by default
    }

    return {
      id: wo.id,
      title: `${wo.number} — ${wo.title}`,
      start: isAllDay
        ? toLocalDateString(rawStartDate, rawStartObj)  // date-only string preserving local date
        : toLocalDateTimeString(clampToVisibleSlot(rawStartObj)),  // clamped into visible slot range
      end: endDate,
      allDay: isAllDay,
      backgroundColor: "#FFFFFF",
      borderColor: typeColor,
      textColor: "#0C1117",
      display: "block",
      extendedProps: { workOrder: wo, isUnscheduled: !hasScheduledStart },
      classNames: opacity < 1 ? [`fc-event-opacity-${Math.round(opacity * 10)}`] : [],
    };
  });
};
